import { useState } from "react";

import { useAtom } from "../../app-jotai";
import { loginDialogStateAtom, authStateAtom } from "../../auth/authAtoms";
import { checkUsername, login } from "../../auth/authApi";

import { saveAuthToken, saveAuthUser } from "../../auth/authStorage";
import { CustomFileStorage } from "../../data/customFileStorage";

import { BackendStatus } from "../BackendStatus";

import "./LoginDialog.scss";

type LoginStep = "username" | "password" | "mfa-setup" | "mfa";

export const LoginDialog = () => {
  const [dialogState, setDialogState] = useAtom(loginDialogStateAtom);
  const [, setAuthState] = useAtom(authStateAtom);

  const [username, setUsername] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<LoginStep>("username");

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await checkUsername(username);

      if (!result.exists) {
        setError("Username not found");
        setIsLoading(false);
        return;
      }

      if (!result.enabled) {
        setError("Account is disabled. Contact your administrator.");
        setIsLoading(false);
        return;
      }

      if (!result.mfaConfigured) {
        setError(
          "MFA not configured for this account. Contact your administrator.",
        );
        setIsLoading(false);
        return;
      }

      // Move to MFA step
      setCurrentStep("mfa");
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check username");
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await login(username, mfaToken);

      // Save to localStorage
      saveAuthToken(result.token);
      saveAuthUser(result.user);

      // Clear the canvas state from localStorage so the app opens to a
      // fresh welcome screen instead of restoring the previous session's drawing
      localStorage.removeItem("excalidraw");
      localStorage.removeItem("excalidraw-state");
      CustomFileStorage.clearCurrentFile();

      // Update auth state
      setAuthState({
        user: result.user,
        token: result.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Close dialog
      setDialogState({ isOpen: false, step: null, username: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setIsLoading(false);
      setMfaToken(""); // Clear the token field
    }
  };

  const handleBack = () => {
    setCurrentStep("username");
    setMfaToken("");
    setError(null);
  };

  if (!dialogState.isOpen) {
    return null;
  }

  return (
    <>
      <BackendStatus />
      <div className="LoginDialog__backdrop" />
      <div className="LoginDialog__modal">
        <div className="LoginDialog__header">
          <h2 className="LoginDialog__title">
            {currentStep === "username"
              ? "Sign In"
              : "Enter Authentication Code"}
          </h2>
        </div>

        <div className="LoginDialog__content">
          {currentStep === "username" ? (
            <>
              <div className="LoginDialog__field">
                <label htmlFor="username" className="LoginDialog__label">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="LoginDialog__input"
                  autoFocus
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleUsernameSubmit(
                        e as unknown as React.FormEvent<HTMLFormElement>,
                      );
                    }
                  }}
                />
              </div>

              {error && <div className="LoginDialog__error">{error}</div>}

              <div className="LoginDialog__info">
                Enter your username to proceed to multi-factor authentication.
              </div>

              <div className="LoginDialog__actions">
                <button
                  className="LoginDialog__button LoginDialog__button--primary"
                  disabled={isLoading || !username.trim()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleUsernameSubmit(
                      e as unknown as React.FormEvent<HTMLFormElement>,
                    );
                  }}
                >
                  {isLoading ? "Checking..." : "Continue"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="LoginDialog__mfa-info">
                <p>Enter the 6-digit code from your authenticator app for:</p>
                <strong>{username}</strong>
              </div>

              <div className="LoginDialog__field">
                <label htmlFor="mfaToken" className="LoginDialog__label">
                  Authentication Code
                </label>
                <input
                  id="mfaToken"
                  type="text"
                  value={mfaToken}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // Only digits
                    if (value.length <= 6) {
                      setMfaToken(value);
                    }
                  }}
                  placeholder="000000"
                  className="LoginDialog__input LoginDialog__input--mfa"
                  autoFocus
                  disabled={isLoading}
                  maxLength={6}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && mfaToken.length === 6) {
                      e.preventDefault();
                      handleMfaSubmit(
                        e as unknown as React.FormEvent<HTMLFormElement>,
                      );
                    }
                  }}
                />
              </div>

              {error && <div className="LoginDialog__error">{error}</div>}

              <div className="LoginDialog__info">
                Open your authenticator app and enter the current code.
              </div>

              <div className="LoginDialog__actions">
                <button
                  className="LoginDialog__button"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Back
                </button>
                <button
                  className="LoginDialog__button LoginDialog__button--primary"
                  disabled={isLoading || mfaToken.length !== 6}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMfaSubmit(
                      e as unknown as React.FormEvent<HTMLFormElement>,
                    );
                  }}
                >
                  {isLoading ? "Verifying..." : "Sign In"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
